import { BubbleFormPage } from '@go-client/components/BubbleUI/BubbleForm';
import GroupBookingsGrid from '@go-client/components/GroupBookingsGrid';
import { isoDate } from '@go-shared/lib/time';
import {
  BareSimpleGroupBooking,
  GroupBooking,
  VenueRequestFile,
  VenueRequestStatus,
} from '@go-shared/types/groupBooking';
import { Property } from '@go-shared/types/property';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import React from 'react';
import EnterBookingPage from '../EnterBookingPage';
import SelectGroupsPage from '../SelectGroupPage';
import SelectItineraryPage from '../SelectItineraryPage';
import {
  BookingForm,
  GroupBookingDataBundle,
  GroupBookingFormWorkflow,
  GroupForm,
  submitBookingForm,
} from '.';
import { User } from '@go-shared/types/user';
import { getGroupBooking, populate } from '@go-client/lib/api/groupBooking';
import BookingConfirmationPage from '../BookingConfirmationPage';
import { getHourFormat } from '@go-shared/lib/property';
import { BrandGroupType } from '@go-shared/types/brand';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';

interface CreateBookingWorkflowPrereqs {
  dataBundle: GroupBookingDataBundle;
  selectedGroup?: GroupBooking;
  selectedVenue?: VenueTemplate;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  bookingForm: BookingForm;
  setBookingForm: React.Dispatch<React.SetStateAction<BookingForm>>;
  clearBookingForm: () => void;
  changeBookingFormAttribute: <T extends keyof BookingForm>(key: T, val: BookingForm[T]) => void;
  groupForm: GroupForm;
  setGroupForm: React.Dispatch<React.SetStateAction<GroupForm>>;
  clearGroupForm: () => void;
  changeGroupFormAttribute: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  property: Property;
  user: User;
  groupTypes: BrandGroupType[];
  isNewGroup: boolean;
  setIsNewGroup: React.Dispatch<React.SetStateAction<boolean>>;
  showDayContext: boolean;
  setShowDayContext: React.Dispatch<React.SetStateAction<boolean>>;
  lastSubmissionTimestamp: number;
  defaultStartDate?: string;
  defaultEndDate?: string;
  languages: string[];
  defaultLanguage: string;
  handleSelectGroup: (group: GroupBooking) => void;
  handleSelectSailingBundle: (sailingBundle: BareSimpleSailingDayBundle) => void;
  handleSelectVenue: (venue: VenueTemplate) => void;
  handleGroupUploadFile: (file: File) => void;
  handleGroupUpdateFile: (file: VenueRequestFile) => void;
  handleGroupDeleteFile: (file: VenueRequestFile) => void;
  handleBookingUploadFile: (file: File) => void;
  handleBookingUpdateFile: (file: VenueRequestFile) => void;
  handleBookingDeleteFile: (file: VenueRequestFile) => void;
  onClose?: () => void;
  onSubmitBooking?: () => void;
}

export default function buildCreateBookingWorkflow(
  pages: Array<BubbleFormPage<GroupBookingDataBundle>>,
  preqs: CreateBookingWorkflowPrereqs
): GroupBookingFormWorkflow {
  const hourFormat = getHourFormat(preqs.property);

  // disabled for now: check if preqs.showDayContext is true
  const prependNode = preqs.selectedSailingBundle && preqs.selectedVenue && (
    <div className="context-view shadow-lg">
      <div className="p-3">
        <DayMarkersButton sailingDay={preqs.selectedSailingBundle.day} />
      </div>
      <div className="italic text-slate px-3">
        <span className="font-bold">TIP:</span> You can click and drag to set the day, start time,
        and end time of this booking!
      </div>
      <GroupBookingsGrid
        key={`${preqs.selectedSailingBundle.sailing._id}-${preqs.selectedVenue._id}-${preqs.lastSubmissionTimestamp}`}
        languages={preqs.languages}
        defaultLanguage={preqs.defaultLanguage}
        hourFormat={hourFormat}
        groupBookingDate={isoDate(preqs.selectedSailingBundle.day.date)}
        groupBookingStartTime={preqs.bookingForm.startTime}
        groupBookingEndTime={preqs.bookingForm.endTime}
        bufferTime={preqs.bookingForm.bufferTime}
        days={preqs.selectedSailingBundle.sailing.days.length}
        sailing={preqs.selectedSailingBundle.sailing}
        venue={preqs.selectedVenue}
        onStartTimeChange={(time, idx) => {
          preqs.changeBookingFormAttribute('startTime', time);

          if (preqs.selectedSailingBundle) {
            const newDay = preqs.selectedSailingBundle.sailing.days[idx];
            preqs.handleSelectSailingBundle({
              ...preqs.selectedSailingBundle,
              day: newDay,
              dayIndex: idx,
              dateIso: isoDate(newDay.date),
            });
          }
        }}
        onEndTimeChange={time => preqs.changeBookingFormAttribute('endTime', time)}
      />
    </div>
  );

  async function handleSubmitBookingForm(status?: VenueRequestStatus) {
    if (!preqs.selectedVenue || !preqs.selectedSailingBundle) {
      throw new Error(`Tried to submit a book without a venue or sailing selected!`);
    }

    await submitBookingForm({
      property: preqs.property,
      user: preqs.user,
      type: 'create',
      selectedGroup: preqs.selectedGroup,
      selectedVenue: preqs.selectedVenue,
      selectedSailingBundle: preqs.selectedSailingBundle,
      bookingDetailsForm: preqs.bookingForm,
      groupDetailsForm: preqs.groupForm,
      status: status || 'pending',
      newGroup: preqs.isNewGroup,
    });

    preqs.changeBookingFormAttribute('startTime', undefined);
    preqs.changeBookingFormAttribute('endTime', undefined);

    if (preqs.onSubmitBooking) preqs.onSubmitBooking();
  }

  async function handleOnSelectGroup(simpleGroup: BareSimpleGroupBooking) {
    const bareGroupBooking = await getGroupBooking(simpleGroup._id);
    const groupBooking = populate(
      bareGroupBooking,
      preqs.property,
      preqs.dataBundle.allUsers,
      preqs.dataBundle.venueTemplates,
      preqs.dataBundle.groupResources
    );
    preqs.handleSelectGroup(groupBooking);
  }

  pages.push({
    key: 'create-page-1',
    render: actions => (
      <SelectGroupsPage
        title={
          preqs.isNewGroup
            ? 'Create Group Booking <span class="text-crimson">(New Group)</span>'
            : 'Create Group Booking'
        }
        pageIndex={1}
        selectedGroup={preqs.selectedGroup}
        groups={preqs.dataBundle.simpleGroupBookings}
        groupForm={preqs.groupForm}
        changeGroupForm={preqs.changeGroupFormAttribute}
        clearGroupForm={preqs.clearGroupForm}
        groupTypeOpts={preqs.groupTypes}
        showDayContext={preqs.showDayContext}
        onToggleShowDayContext={() => preqs.setShowDayContext(!preqs.showDayContext)}
        isNewGroup={preqs.isNewGroup}
        setIsNewGroup={preqs.setIsNewGroup}
        nextTitle={
          preqs.isNewGroup
            ? 'Next: Select Voyage & Venue (New Group)'
            : 'Next: Select Voyage & Venue'
        }
        onUpdateFile={preqs.handleGroupUpdateFile}
        onUploadFile={preqs.handleGroupUploadFile}
        onDeleteFile={preqs.handleGroupDeleteFile}
        onClose={preqs.onClose}
        onSelectGroup={handleOnSelectGroup}
        onGoNext={actions.goForward}
      />
    ),
  });

  pages.push({
    key: 'create-page-2',
    render: actions => (
      <SelectItineraryPage
        pageIndex={2}
        sailings={preqs.dataBundle.simpleSailings}
        venueTemplates={preqs.dataBundle.venueTemplates}
        isNewGroup={preqs.isNewGroup}
        groupForm={preqs.groupForm}
        selectedGroup={preqs.selectedGroup}
        selectedSailingBundle={preqs.selectedSailingBundle}
        selectedVenue={preqs.selectedVenue}
        showDayContext={preqs.showDayContext}
        onSelectSailingBundle={preqs.handleSelectSailingBundle}
        handleOnSelectVenue={preqs.handleSelectVenue}
        onToggleShowDayContext={() => preqs.setShowDayContext(!preqs.showDayContext)}
        defaultStartDate={preqs.defaultStartDate}
        defaultEndDate={preqs.defaultEndDate}
        onGoBack={actions.goBack}
        onGoNext={actions.goForward}
        onClose={preqs.onClose}
      />
    ),
  });

  pages.push({
    key: 'create-page-3',
    render: actions =>
      preqs.selectedSailingBundle &&
      preqs.selectedVenue && (
        <EnterBookingPage
          title={
            preqs.isNewGroup
              ? 'Create Group Booking <span class="text-danger">(New Group)</span>'
              : 'Create Group Booking'
          }
          pageIndex={3}
          bookingForm={preqs.bookingForm}
          changeBookingForm={preqs.changeBookingFormAttribute}
          clearBookingForm={preqs.clearBookingForm}
          groupResources={preqs.dataBundle.groupResources}
          isNewGroup={preqs.isNewGroup}
          groupForm={preqs.groupForm}
          selectedGroup={preqs.selectedGroup}
          selectedSailingBundle={preqs.selectedSailingBundle}
          selectedVenue={preqs.selectedVenue}
          venueConfigOpts={preqs.selectedVenue.configurations}
          showDayContext={preqs.showDayContext}
          submitTitle={
            preqs.isNewGroup
              ? 'Finish: Create Group Booking (New Group)'
              : 'Finish: Create Group Booking'
          }
          onUploadFile={preqs.handleBookingUploadFile}
          onDeleteFile={preqs.handleBookingDeleteFile}
          onUpdateFile={preqs.handleBookingUpdateFile}
          onGoBack={actions.goBack}
          onGoForward={actions.goForward}
          onClose={preqs.onClose}
          onToggleShowDayContext={() => preqs.setShowDayContext(!preqs.showDayContext)}
          onSubmitBookingForm={handleSubmitBookingForm}
        />
      ),
  });

  pages.push({
    key: 'create-page-4',
    render: actions => (
      <BookingConfirmationPage
        title="Edit Group Booking"
        pageIndex={4}
        isNewGroup={preqs.isNewGroup}
        groupForm={preqs.groupForm}
        onClose={preqs.onClose}
        onGoStart={actions.goStart}
      />
    ),
  });

  return { prependNode };
}
